import { useCallback, useEffect, useMemo, useState } from 'react';
import { restGet } from '@/lib/supabase';
import { useStore } from '@/context/StoreContext';
import './AdminAnalyticsV5.css';

type EventRow={
  id:string;
  event_name:string;
  session_id:string|null;
  visitor_id:string|null;
  path:string|null;
  source:string|null;
  medium:string|null;
  campaign:string|null;
  device:string|null;
  search_term:string|null;
  product_slug:string|null;
  metadata:Record<string,unknown>|null;
  created_at:string;
};

const sinceIso=(days:number)=>{const d=new Date();d.setDate(d.getDate()-days);return d.toISOString()};
const pct=(value:number,total:number)=>total?`${(value/total*100).toFixed(1)}%`:'0%';
const label:Record<string,string>={page_view:'צפייה בעמוד',product_view:'צפייה במוצר',search:'חיפוש',add_to_cart:'הוספה לסל',begin_checkout:'התחלת קופה',purchase:'רכישה',whatsapp_click:'WhatsApp'};

const routeLabel=(path:string|null,productSlug:string|null,products:Array<{slug:string;name:string}>)=>{
  if(productSlug){
    const product=products.find(item=>item.slug===productSlug);
    return product?.name||productSlug;
  }
  const raw=String(path||'');
  const [pathname,query='']=raw.split('?');
  const params=new URLSearchParams(query);
  if(pathname==='/'||pathname==='')return 'דף הבית';
  if(pathname==='/catalog'){
    const q=params.get('q');
    if(q)return `תוצאות חיפוש: ${q}`;
    if(params.get('sale')==='1')return 'SALE';
    const category=params.get('category')||'';
    const categories:Record<string,string>={new:'חדשים באתר',rings:'טבעות',earrings:'עגילים',necklaces:'שרשראות',bracelets:'צמידים'};
    return categories[category]||'כל התכשיטים';
  }
  const labels:Record<string,string>={
    '/cart':'סל קניות','/checkout':'קופה','/wishlist':'מועדפים','/account':'אזור אישי',
    '/ring-builder':'בונה הטבעת','/journal':'המגזין','/about':'אודות','/contact':'צור קשר',
    '/size-guide':'מדריך מידות','/stores':'מרכז הידע','/collections':'קולקציות',
    '/payment/success':'תשלום הושלם','/payment/failed':'תשלום נכשל',
  };
  if(labels[pathname])return labels[pathname];
  const productMatch=pathname.match(/^\/product\/(.+)$/);
  if(productMatch){
    const slug=decodeURIComponent(productMatch[1]);
    const product=products.find(item=>item.slug===slug);
    return product?.name||slug;
  }
  return raw||'—';
};

export function AdminAnalytics(){
  const {products}=useStore();
  const [events,setEvents]=useState<EventRow[]>([]);
  const [days,setDays]=useState(30);
  const [loading,setLoading]=useState(true);
  const [ready,setReady]=useState(true);

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const rows=await restGet<EventRow[]>('mielley_analytics_events',`select=id,event_name,session_id,visitor_id,path,source,medium,campaign,device,search_term,product_slug,metadata,created_at&created_at=gte.${encodeURIComponent(sinceIso(days))}&order=created_at.desc&limit=10000`);
      setEvents(rows);setReady(true);
    }catch{setEvents([]);setReady(false)}finally{setLoading(false)}
  },[days]);

  useEffect(()=>{void load()},[load]);

  const metrics=useMemo(()=>{
    const sessions=new Set(events.map(e=>e.session_id).filter(Boolean)).size;
    const visitors=new Set(events.map(e=>e.visitor_id).filter(Boolean)).size;
    const views=events.filter(e=>e.event_name==='page_view').length;
    const products=events.filter(e=>e.event_name==='product_view').length;
    const carts=events.filter(e=>e.event_name==='add_to_cart').length;
    const checkout=events.filter(e=>e.event_name==='begin_checkout').length;
    const purchase=events.filter(e=>e.event_name==='purchase').length;
    const productSessions=new Set(events.filter(e=>e.event_name==='product_view').map(e=>e.session_id).filter(Boolean)).size;
    const cartSessions=new Set(events.filter(e=>e.event_name==='add_to_cart').map(e=>e.session_id).filter(Boolean)).size;
    const checkoutSessions=new Set(events.filter(e=>e.event_name==='begin_checkout').map(e=>e.session_id).filter(Boolean)).size;
    const purchaseSessions=new Set(events.filter(e=>e.event_name==='purchase').map(e=>e.session_id).filter(Boolean)).size;
    return {sessions,visitors,views,products,carts,checkout,purchase,productSessions,cartSessions,checkoutSessions,purchaseSessions};
  },[events]);

  const grouped=(key:'source'|'device'|'path')=>{
    const map=new Map<string,number>();
    events.filter(e=>e.event_name==='page_view').forEach(e=>{
      const raw=String(e[key]||'לא ידוע');
      if(key==='source'&&raw==='Internal')return;
      map.set(raw,(map.get(raw)||0)+1);
    });
    return [...map.entries()].map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count);
  };

  const sources=useMemo(()=>grouped('source').slice(0,10),[events]);
  const devices=useMemo(()=>grouped('device').slice(0,5),[events]);
  const pages=useMemo(()=>grouped('path').slice(0,12),[events]);

  const daily=useMemo(()=>{
    const out:Array<{label:string,count:number}>=[];
    for(let i=days-1;i>=0;i--){
      const d=new Date();d.setDate(d.getDate()-i);d.setHours(0,0,0,0);
      const next=new Date(d);next.setDate(next.getDate()+1);
      const count=events.filter(e=>e.event_name==='page_view'&&new Date(e.created_at)>=d&&new Date(e.created_at)<next).length;
      out.push({label:new Intl.DateTimeFormat('he-IL',{day:'2-digit',month:'2-digit'}).format(d),count});
    }
    return out;
  },[events,days]);
  const dailyMax=Math.max(1,...daily.map(d=>d.count));

  const recent=events.slice(0,30);

  return <div className="maa5-page" dir="rtl">
    <section className="maa5-title"><div><span>LIVE ANALYTICS</span><h2>אנליטיקה והתנהגות באתר</h2><p>כניסות, מקורות תנועה, מכשירים, עמודים ומשפך רכישה.</p></div><div><select value={days} onChange={e=>setDays(Number(e.target.value))}><option value={7}>7 ימים</option><option value={30}>30 ימים</option><option value={90}>90 ימים</option></select><button onClick={()=>void load()}>↻ רענון</button></div></section>

    {!ready&&<section className="maa5-setup"><b>Analytics עדיין לא הופעל.</b><span>יש להריץ ב־Supabase את SUPABASE-MIELLEY-ANALYTICS-V5.sql שמגיע בחבילת ההתקנה.</span></section>}

    <section className="maa5-kpis">
      <article><span>סשנים</span><b>{ready?metrics.sessions:'—'}</b><small>{days} הימים האחרונים</small></article>
      <article><span>מבקרים</span><b>{ready?metrics.visitors:'—'}</b><small>מזהה אנונימי בלבד</small></article>
      <article><span>צפיות עמוד</span><b>{ready?metrics.views:'—'}</b><small>{metrics.sessions?(metrics.views/metrics.sessions).toFixed(1):'0'} צפיות לסשן</small></article>
      <article><span>צפיות מוצר</span><b>{ready?metrics.products:'—'}</b><small>{pct(metrics.products,metrics.views)} מכל הצפיות</small></article>
      <article><span>הוספות לסל</span><b>{ready?metrics.carts:'—'}</b><small>{pct(metrics.carts,metrics.products)} מצפיות מוצר</small></article>
    </section>

    <section className="maa5-grid two">
      <article className="maa5-card maa5-chart"><header><span>TRAFFIC TREND</span><h3>תנועה לאורך זמן</h3></header><div className="maa5-bars">{daily.map((d,index)=><div key={`${d.label}-${index}`} title={`${d.label}: ${d.count}`}><i style={{height:`${Math.max(d.count?8:2,d.count/dailyMax*100)}%`}}/><small>{days<=30||index%3===0?d.label:''}</small></div>)}</div></article>
      <article className="maa5-card"><header><span>FUNNEL</span><h3>משפך המרה לפי סשנים</h3></header><div className="maa5-funnel">
        <div><b>{metrics.sessions}</b><span>סשנים באתר</span><em>100%</em></div>
        <div><b>{metrics.productSessions}</b><span>צפו במוצר</span><em>{pct(metrics.productSessions,metrics.sessions)}</em></div>
        <div><b>{metrics.cartSessions}</b><span>הוסיפו לסל</span><em>{pct(metrics.cartSessions,metrics.sessions)}</em></div>
        <div><b>{metrics.checkoutSessions}</b><span>התחילו Checkout</span><em>{pct(metrics.checkoutSessions,metrics.sessions)}</em></div>
        <div><b>{metrics.purchaseSessions}</b><span>הגיעו להצלחת תשלום</span><em>{pct(metrics.purchaseSessions,metrics.sessions)}</em></div>
      </div></article>
    </section>

    <section className="maa5-grid three">
      <article className="maa5-card"><header><span>SOURCES</span><h3>מקורות כניסה</h3></header><div className="maa5-list">{sources.map(item=><div key={item.name}><span>{item.name}</span><b>{item.count}</b><small>{pct(item.count,metrics.views)}</small></div>)}</div></article>
      <article className="maa5-card"><header><span>DEVICES</span><h3>מכשירים</h3></header><div className="maa5-list">{devices.map(item=><div key={item.name}><span>{item.name==='mobile'?'מובייל':item.name==='tablet'?'טאבלט':item.name==='desktop'?'מחשב':item.name}</span><b>{item.count}</b><small>{pct(item.count,metrics.views)}</small></div>)}</div></article>
      <article className="maa5-card"><header><span>TOP PAGES</span><h3>עמודים מובילים</h3></header><div className="maa5-list pages">{pages.map(item=><div key={item.name}><span title={item.name}>{routeLabel(item.name,null,products)}</span><b>{item.count}</b></div>)}</div></article>
    </section>

    <section className="maa5-card maa5-feed"><header><span>LIVE FEED</span><h3>פעילות אחרונה</h3></header>{loading&&!events.length?<div className="maa5-empty">טוען נתונים…</div>:recent.length?<div className="maa5-feed-list">{recent.map(event=><div key={event.id}><i/><span><b>{label[event.event_name]||event.event_name}</b><small>{event.search_term?`חיפוש: ${event.search_term}`:routeLabel(event.path,event.product_slug,products)}</small></span><span>{event.source||'Direct'}</span><span>{event.device==='mobile'?'מובייל':event.device==='tablet'?'טאבלט':event.device==='desktop'?'מחשב':event.device||'—'}</span><time>{new Intl.DateTimeFormat('he-IL',{dateStyle:'short',timeStyle:'short'}).format(new Date(event.created_at))}</time></div>)}</div>:<div className="maa5-empty">עדיין אין אירועים להצגה.</div>}</section>
  </div>;
}
